import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Row, Col, Card, Button, Table, Badge, Spinner, Alert } from 'react-bootstrap'
import * as FaIcons from 'react-icons/fa'
import { useSelector } from 'react-redux'
import Swal from 'sweetalert2'

import SimpleMantenedorLayout from '../../../components/SimpleMantenedorLayout'
import { useMantenedores } from '../../../context/MantenedoresContext'
import {
    getEmailTemplates,
    deleteEmailTemplate
} from '@helpers/api/mantenedores/emailTemplates'
import { SuccessSwal, ErrorSwal } from '@helpers/swal'

const EmailTemplatesPage = () => {
    const history = useHistory()
    const user = useSelector(state => state.auth.user)
    const { userPermissions } = useMantenedores()
    const permisos = userPermissions?.modules?.cierre || {}

    const [templates, setTemplates] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const fetchTemplates = async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await getEmailTemplates(user)
            if (res.success) {
                setTemplates(res.data)
            } else {
                setError(res.error)
            }
        } catch (e) {
            setError('Error al cargar los templates')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchTemplates()
    }, [])

    const handleCreate = () => {
        history.push('/mantenedores/cierre/email-templates/nuevo')
    }

    const handleEdit = (template) => {
        history.push(`/mantenedores/cierre/email-templates/${template.id}`)
    }

    const handleDelete = async (template) => {
        const result = await Swal.fire({
            title: '¿Eliminar template?',
            text: `¿Deseas eliminar el template "${template.nombre}"? Esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33'
        })
        if (!result.isConfirmed) return

        try {
            const res = await deleteEmailTemplate(user, template.id)
            if (res.success) {
                SuccessSwal('Eliminado', 'Template eliminado correctamente')
                fetchTemplates()
            } else {
                ErrorSwal('Error', res.error || 'No se pudo eliminar el template')
            }
        } catch (e) {
            ErrorSwal('Error', 'Error inesperado al eliminar el template')
        }
    }

    return (
        <SimpleMantenedorLayout
            mantenedorId="cierre"
            title="Templates de Correo"
            subtitle="Gestión de plantillas de correo electrónico para envíos de cierre"
            iconColor="#7b1fa2"
            breadcrumbs={[
                { label: 'Cierre', path: '/mantenedores/cierre' },
                { label: 'Templates de Correo' }
            ]}
            showBackButton={false}
        >
            <Row className="mb-4">
                <Col xs={12} className="d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="mb-0">Plantillas de correo</h5>
                        <small className="text-muted">
                            Cada plantilla define el asunto, campos personalizados y el HTML enviado a conductores y empresas.
                        </small>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={fetchTemplates}
                            disabled={loading}
                            title="Actualizar"
                        >
                            <FaIcons.FaSync className={loading ? 'fa-spin' : ''} size={12} />
                        </Button>
                        {(permisos.canCreate || permisos.canEdit) && (
                            <Button variant="success" onClick={handleCreate}>
                                <FaIcons.FaPlus className="me-2" />
                                Nuevo Template
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" className="mb-3">
                    <FaIcons.FaExclamationTriangle className="me-2" />
                    {error}
                </Alert>
            )}

            <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2 text-muted">Cargando templates...</div>
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <FaIcons.FaEnvelopeOpen size={40} className="mb-3 opacity-50" />
                            <div>No hay templates de correo definidos</div>
                            {(permisos.canCreate || permisos.canEdit) && (
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="mt-3"
                                    onClick={handleCreate}
                                >
                                    Crear primer template
                                </Button>
                            )}
                        </div>
                    ) : (
                        <Table hover responsive className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Nombre</th>
                                    <th>Slug</th>
                                    <th>Asunto</th>
                                    <th>Estado</th>
                                    <th>Blob Path</th>
                                    <th style={{ width: 130 }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {templates.map(t => (
                                    <tr key={t.id}>
                                        <td><strong>{t.nombre}</strong></td>
                                        <td>
                                            <code className="small">{t.slug}</code>
                                        </td>
                                        <td className="small">{t.asunto}</td>
                                        <td>
                                            <Badge bg={t.activo ? 'success' : 'secondary'}>
                                                {t.activo ? 'Activo' : 'Inactivo'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <small className="text-muted font-monospace">{t.blob_path}</small>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2 align-items-center" style={{ flexWrap: 'nowrap' }}>
                                                <FaIcons.FaEdit
                                                    title="Editar template"
                                                    style={{ color: '#0d6efd', cursor: 'pointer', fontSize: '1.2em' }}
                                                    onClick={() => handleEdit(t)}
                                                />
                                                {(permisos.canCreate || permisos.canEdit) && (
                                                    <FaIcons.FaTrash
                                                        title="Eliminar template"
                                                        style={{ color: '#dc3545', cursor: 'pointer', fontSize: '1.1em' }}
                                                        onClick={() => handleDelete(t)}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            <Row className="mt-4">
                <Col xs={12}>
                    <Card className="border-0 bg-light">
                        <Card.Body className="p-3">
                            <small className="text-muted">
                                <FaIcons.FaInfoCircle className="me-1 text-info" />
                                Los templates usan variables <code>{'{nombre_campo}'}</code> que se reemplazan automáticamente al enviar.
                                Los campos <strong>campos_auto_cierre</strong> se llenan desde los datos del cierre (fechas, totales, etc.)
                                Los campos <strong>campos_config</strong> son configurados por el usuario en el modal de envío.
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </SimpleMantenedorLayout>
    )
}

export default EmailTemplatesPage
